/**
 * Login Sidebar Component
 * 處理從右側滑出的會員登入 sidebar
 */

(function() {
  'use strict';

  if (!customElements.get('login-sidebar')) {
    const selectors = {
      drawerInner: '[data-drawer-inner]',
      drawerClose: '[data-drawer-close]',
      focusable: 'button, [href], select, textarea, input, [tabindex]:not([tabindex="-1"])',
    };

    const classes = {
      animated: 'drawer--animated',
      open: 'is-open',
      closing: 'is-closing',
    };

    // 動畫處理輔助函數
    const handleAnimation = (element, callback, fallbackDelay = 350) => {
      if (window.theme?.waitForAnimationEnd) {
        return window.theme.waitForAnimationEnd(element).then(callback);
      } else {
        const handleEnd = () => callback();
        element.addEventListener('animationend', handleEnd, { once: true });
        setTimeout(() => {
          if (element.isConnected) {
            handleEnd();
          }
        }, fallbackDelay);
      }
    };

    customElements.define(
      'login-sidebar',
      class LoginSidebar extends HTMLElement {
        constructor() {
          super();

          this.a11y = window.theme?.a11y || {
            trapFocus: () => {},
            removeTrapFocus: () => {},
          };
          this.isAnimating = false;
          this.drawerInner = this.querySelector(selectors.drawerInner);
          this.triggerButton = null;
          this.boundHandlers = {
            showDrawer: this.showDrawer.bind(this),
            hideDrawer: this.hideDrawer.bind(this),
            handleToggle: this.handleToggle.bind(this),
            handleDrawerOpen: this.handleDrawerOpen.bind(this),
            handleEscape: this.handleEscape.bind(this),
            handleCloseClick: this.handleCloseClick.bind(this),
          };

          this.connectDrawer();
          this.closers();
        }

        handleToggle(e) {
          const triggerButton = e.detail?.button;
          
          // 只處理來自 login sidebar 觸發按鈕的事件
          // 過濾掉漢堡選單等其他 drawer 的觸發事件
          if (!triggerButton) {
            // 沒有觸發按鈕，忽略此事件
            // 注意：這不會阻止其他 drawer 處理，因為事件是直接發送到特定元素的
            return;
          }

          // 檢查是否為 login sidebar 的觸發按鈕
          const isLoginSidebarTrigger = triggerButton.hasAttribute('data-trigger-login-sidebar') ||
                                       triggerButton.hasAttribute('data-account-trigger');
          
          // 檢查是否為其他 drawer 的觸發按鈕（如漢堡選單）
          const isOtherDrawerTrigger = triggerButton.hasAttribute('data-drawer-toggle') &&
                                      !triggerButton.hasAttribute('data-trigger-login-sidebar') &&
                                      !triggerButton.hasAttribute('data-account-trigger');
          
          if (!isLoginSidebarTrigger || isOtherDrawerTrigger) {
            // 不是 login sidebar 的觸發按鈕，忽略此事件
            // 如果這是其他 drawer 的觸發按鈕，需要重新發送到正確的 drawer
            if (isOtherDrawerTrigger) {
              // 找到正確的 drawer（header-drawer）並發送事件
              const targetDrawer = document.querySelector('header-drawer[data-drawer]');
              if (targetDrawer && targetDrawer !== this) {
                targetDrawer.dispatchEvent(
                  new CustomEvent('theme:drawer:toggle', {
                    bubbles: false,
                    detail: {
                      button: triggerButton,
                    },
                  })
                );
              }
            }
            return;
          }

          this.triggerButton = triggerButton;

          if (this.classList.contains(classes.open)) {
            this.dispatchEvent(
              new CustomEvent('theme:drawer:close', {
                bubbles: true,
              })
            );
          } else {
            this.dispatchEvent(
              new CustomEvent('theme:drawer:open', {
                bubbles: true,
              })
            );
          }
        }

        handleDrawerOpen(e) {
          if (e.target !== this && e.target.closest('header-drawer')) {
            this.hideDrawer();
          }
        }

        connectDrawer() {
          // 監聽 toggle 事件
          this.addEventListener('theme:drawer:toggle', this.boundHandlers.handleToggle);
          this.addEventListener('theme:drawer:close', this.boundHandlers.hideDrawer);
          this.addEventListener('theme:drawer:open', this.boundHandlers.showDrawer);

          // 當其他 drawer 打開時，關閉 login sidebar
          document.addEventListener('theme:cart-drawer:open', this.boundHandlers.hideDrawer);
          document.addEventListener('theme:drawer:open', this.boundHandlers.handleDrawerOpen);
        }

        handleCloseClick() {
          this.hideDrawer();
        }

        handleEscape(event) {
          if (event.code === 'Escape' && this.classList.contains(classes.open)) {
            this.hideDrawer();
          }
        }

        /**
         * 設置關閉 sidebar 的事件監聽器
         * - 關閉按鈕點擊
         * - ESC 鍵
         * - 遮罩點擊（通過偽元素冒泡到觸發按鈕）
         */
        closers() {
          // 關閉按鈕
          this.querySelectorAll(selectors.drawerClose)?.forEach((button) => {
            button.addEventListener('click', this.boundHandlers.handleCloseClick);
          });

          // ESC 鍵關閉
          document.addEventListener('keyup', this.boundHandlers.handleEscape);

          // 點擊遮罩關閉：偽元素是觸發按鈕的 ::after，點擊會冒泡到按鈕，觸發按鈕的點擊事件會關閉 sidebar
        }

        showDrawer() {
          if (this.isAnimating || !this.drawerInner) return;

          this.isAnimating = true;

          // 更新所有觸發按鈕的 aria-expanded 狀態
          this.updateTriggerButtons('true');
          
          this.classList.add(classes.open, classes.animated);

          // 鎖定頁面滾動
          document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));

          this.a11y.removeTrapFocus();

          // 等待動畫結束後聚焦
          handleAnimation(this.drawerInner, () => {
            this.isAnimating = false;

            const firstFocusable = this.querySelector(selectors.focusable);
            if (firstFocusable) {
              this.a11y.trapFocus(this.drawerInner, {
                elementToFocus: firstFocusable,
              });
            }
          });
        }

        /**
         * 更新所有觸發按鈕的 aria-expanded 狀態
         * 確保 header 和 sticky navigation 的按鈕狀態同步
         */
        updateTriggerButtons(expanded) {
          const allTriggers = document.querySelectorAll(
            '[data-trigger-login-sidebar], [data-account-trigger]'
          );
          allTriggers.forEach((button) => {
            button.setAttribute('aria-expanded', expanded);
          });
        }

        hideDrawer() {
          if (this.isAnimating || !this.classList.contains(classes.open) || !this.drawerInner) return;

          this.isAnimating = true;

          this.classList.add(classes.closing);
          this.classList.remove(classes.open);

          this.a11y.removeTrapFocus();

          // 更新所有觸發按鈕的 aria-expanded 狀態
          this.updateTriggerButtons('false');

          // 解鎖頁面滾動
          document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

          // 等待動畫結束
          handleAnimation(this.drawerInner, () => {
            this.classList.remove(classes.closing, classes.animated);
            this.isAnimating = false;
          });
        }

        disconnectedCallback() {
          // 清理所有事件監聽器
          this.removeEventListener('theme:drawer:toggle', this.boundHandlers.handleToggle);
          this.removeEventListener('theme:drawer:close', this.boundHandlers.hideDrawer);
          this.removeEventListener('theme:drawer:open', this.boundHandlers.showDrawer);
          document.removeEventListener('theme:cart-drawer:open', this.boundHandlers.hideDrawer);
          document.removeEventListener('theme:drawer:open', this.boundHandlers.handleDrawerOpen);
          document.removeEventListener('keyup', this.boundHandlers.handleEscape);

          // 清理關閉按鈕事件
          this.querySelectorAll(selectors.drawerClose)?.forEach((button) => {
            button.removeEventListener('click', this.boundHandlers.handleCloseClick);
          });

          // 移除焦點陷阱
          this.a11y.removeTrapFocus();
        }
      }
    );
  }

  /**
   * 處理觸發按鈕的點擊事件
   * 注意：sticky navigation 的 account 按鈕有自己的處理邏輯，不需要在這裡處理
   */
  const triggerHandlers = new WeakMap();
  const TRIGGER_SELECTOR = '[data-trigger-login-sidebar]';
  const SIDEBAR_SELECTOR = 'login-sidebar[data-drawer="login"]';

  function handleTriggerClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const loginSidebar = document.querySelector(SIDEBAR_SELECTOR);
    if (!loginSidebar) return;

    loginSidebar.dispatchEvent(
      new CustomEvent('theme:drawer:toggle', {
        bubbles: false,
        detail: {
          button: e.currentTarget,
        },
      })
    );
  }

  function initLoginSidebarTriggers() {
    const triggers = document.querySelectorAll(TRIGGER_SELECTOR);
    const loginSidebar = document.querySelector(SIDEBAR_SELECTOR);

    if (!loginSidebar) return;

    triggers.forEach((button) => {
      // 避免重複綁定
      if (triggerHandlers.has(button)) return;

      const handler = handleTriggerClick;
      button.addEventListener('click', handler);
      triggerHandlers.set(button, handler);
    });
  }

  // 當 DOM 載入完成時初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initLoginSidebarTriggers);
  } else {
    initLoginSidebarTriggers();
  }

  // 監聽動態載入的內容（主題編輯器）
  document.addEventListener('shopify:section:load', initLoginSidebarTriggers);
})();

